// Ops → ECAP Admin console (super-admin only). Centralises the things that used to clutter every screen:
// company (tenant) provisioning + user management (register, reset password, group assignment). Non-admins
// never see this — the nav item is hidden and the API 403s. See src/pages/README.md.
import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader, Button, Alert, ConfirmDialog } from '../ui/primitives';
import {
  whoami, listTenants, provisionTenant,
  adminListUsers, adminCreateUser, adminResetPassword,
  listCompanyUsers, createCompanyUser, setCompanyUserEnabled, resetCompanyUserPassword, deleteCompanyUser,
  listMovorraStaff, inviteMovorraStaff,
  adminSetUserEnabled, adminDeleteUser, activeTenant,
  getTenantUsage,
  getTenantConsoleAccess, setTenantConsoleAccess,
  getLlmConfig, setLlmConfig,
  getPremiumTenant, setPremiumGrants, getTenantBilling,
  getMigrationPlan, runMigration, runMigrationFor,
  getTenantEndpoint, setTenantEndpoint, clearEndpointCache,
  listCognitoGroups, ensureTenantCognitoGroup,
  getTenantSecrets, provisionTenantSecrets,
} from '../services/ecapApi';

const inp = 'rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm';

// `activeCompany` is the Studio sidebar's company. Panels scoped to one company are keyed on it so a switch
// remounts them (clearing the previous company's rows) and they re-load for the new company.
function OpsEcapAdmin({ authSession, activeCompany }) {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('companies');
  useEffect(() => { whoami().then(setMe).catch(() => setMe({ admin: false })); }, []);

  if (me && !me.admin) {
    return (
      <div className="p-6">
        <PageHeader title="Admin" subtitle="Platform administration" />
        <Alert tone="warning">You don't have access to the admin console.</Alert>
      </div>
    );
  }

  return (
    <div className="p-6">
      <PageHeader title="Admin console" subtitle="Companies & users — Movorra super-admin only"
        right={<span className="text-[11px] text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded">super-admin</span>} />
      <div className="flex gap-1.5 mb-4 border-b border-slate-200">
        {[['companies', 'Companies'], ['access', 'Access & Premium'], ['users', 'Users'], ['billing', 'Billing'], ['llm', 'AI Model'], ['db', 'Database']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === k ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>{l}</button>
        ))}
      </div>
      {tab === 'companies' && <Companies authSession={authSession} />}
      {tab === 'access' && <AccessControl key={activeCompany} authSession={authSession} activeCompany={activeCompany} />}
      {tab === 'users' && <Users authSession={authSession} activeCompany={activeCompany} />}
      {tab === 'billing' && <Billing key={activeCompany} authSession={authSession} activeCompany={activeCompany} />}
      {tab === 'llm' && <LlmConfig authSession={authSession} />}
      {tab === 'db' && <Migration authSession={authSession} />}
    </div>
  );
}

function Companies({ authSession }) {
  const [rows, setRows] = useState([]);
  const [id, setId] = useState(''); const [name, setName] = useState(''); const [adminEmail, setAdminEmail] = useState('');
  const [template, setTemplate] = useState('quote'); // B5: which starter flow the new company begins with
  const [error, setError] = useState(null); const [msg, setMsg] = useState(null); const [busy, setBusy] = useState(false);
  const load = useCallback(() => { listTenants(authSession).then((t) => setRows(t || [])).catch((e) => setError(e.message)); }, [authSession]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!id.trim()) return;
    setBusy(true); setError(null); setMsg(null);
    try {
      const r = await provisionTenant(id.trim(), name.trim() || id.trim(), adminEmail.trim(), undefined, template);
      // One click created the schema + Cognito group + secret vault; report the optional admin invite.
      let note = `Provisioned ${r?.tenant?.name || id.trim()} — schema, Cognito group & secret vault ready.`;
      if (r?.invited) note += ` Invited ${r.invited}.`;
      else if (r?.inviteError) note += ` (invite failed: ${r.inviteError})`;
      setMsg(note);
      setId(''); setName(''); setAdminEmail(''); load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl">
      {error && <Alert tone="danger" className="mb-3">{error}</Alert>}
      {msg && <Alert tone="success" className="mb-3">{msg}</Alert>}
      <div className="flex flex-wrap gap-2 mb-2 items-end">
        <label className="text-xs text-slate-500">tenant id<input className={`${inp} block mt-1`} value={id} placeholder="globex" onChange={(e) => setId(e.target.value)} /></label>
        <label className="text-xs text-slate-500">company name<input className={`${inp} block mt-1`} value={name} placeholder="Globex Corp" onChange={(e) => setName(e.target.value)} /></label>
        <label className="text-xs text-slate-500">admin email <span className="text-slate-400">(optional invite)</span><input className={`${inp} block mt-1`} value={adminEmail} placeholder="admin@globex.com" onChange={(e) => setAdminEmail(e.target.value)} /></label>
        <label className="text-xs text-slate-500" title="The ready-to-customize flow the company starts with">starter template
          <select className={`${inp} block mt-1`} value={template} onChange={(e) => setTemplate(e.target.value)}>
            <option value="quote">Quote request (typed intake)</option>
            <option value="starter">Contact capture</option>
            <option value="blank">Blank — no starter flow</option>
          </select>
        </label>
        <Button size="sm" onClick={create} disabled={busy}>{busy ? 'Provisioning…' : '+ Provision company'}</Button>
      </div>
      <div className="text-[11px] text-slate-400 mb-2">One click provisions the company&apos;s schema, Cognito group &amp; secret vault (and invites the admin if an email is given). Manage screen access &amp; premium in the <b>Access &amp; Premium</b> tab.</div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs"><tr><th className="text-left px-3 py-2 font-medium">Company</th><th className="text-left px-3 py-2 font-medium">id</th><th className="text-left px-3 py-2 font-medium">Flows</th></tr></thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.id} className="border-t border-slate-100">
              <td className="px-3 py-2 font-medium text-slate-800">{t.name || t.id}</td>
              <td className="px-3 py-2 text-slate-500">{t.id}</td>
              <td className="px-3 py-2 text-slate-600">{t.flowCount}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={3} className="px-3 py-3 text-slate-400">No companies yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// The single access-control panel: one place to decide, per company, BOTH what they can see (screen access)
// and what premium capabilities they may use (entitlements). Pick a company, control everything for it.
function AccessControl({ authSession, activeCompany }) {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState(null);
  const [cognitoGroups, setCognitoGroups] = useState(null); // null = unknown (Cognito unreachable), else array of names
  // Open the sidebar's company by default; fall back to the first one if it isn't in the list.
  useEffect(() => { listTenants(authSession).then((t) => { setRows(t || []); setOpenId((c) => c || ((t || []).some((x) => x.id === activeCompany) ? activeCompany : (t && t[0] ? t[0].id : null))); }).catch((e) => setError(e.message)); }, [authSession, activeCompany]);
  // One list call for the whole panel — each company derives provisioned/missing from it (best-effort).
  useEffect(() => { listCognitoGroups(authSession).then((d) => setCognitoGroups(d?.groups || [])).catch(() => setCognitoGroups(null)); }, [authSession]);
  return (
    <div className="max-w-3xl">
      {error && <Alert tone="danger" className="mb-3">{error}</Alert>}
      <p className="text-sm text-slate-500 mb-3">Control every company's access in one place — which screens they see (and read-only vs full), and which premium capabilities they may use. All enforced server-side.</p>
      <div className="space-y-2">
        {rows.map((t) => (
          <div key={t.id} className="rounded-xl border border-slate-200 bg-white">
            <button className="w-full flex items-center justify-between px-3 py-2.5 text-left" onClick={() => setOpenId(openId === t.id ? null : t.id)}>
              <span className="font-medium text-slate-800 text-sm">{t.name || t.id} <span className="text-slate-400 font-normal">· {t.id}</span></span>
              <span className="text-xs text-blue-600">{openId === t.id ? 'close' : 'manage access'}</span>
            </button>
            {openId === t.id && (
              <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-4">
                <ConsoleAccess tenantId={t.id} authSession={authSession} />
                <Endpoint tenantId={t.id} authSession={authSession} />
                <Identity tenantId={t.id} authSession={authSession} groups={cognitoGroups} />
                <Secrets tenantId={t.id} authSession={authSession} />
                <Entitlements tenantId={t.id} authSession={authSession} />
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && <div className="text-slate-400 text-sm">No companies yet — add one in the Companies tab.</div>}
      </div>
    </div>
  );
}

// Per-company console access: which ECAP screens the company sees and at what level. Enforced server-side
// (a HIDDEN screen's data is blocked; READ_ONLY blocks writes), so this is real control, not just nav hiding.
const CONSOLE_SCREENS = [
  { key: 'automation', label: 'Automation', hint: 'Flow designer + Library' },
  { key: 'leads', label: 'Leads', hint: 'Captured sessions' },
  { key: 'analytics', label: 'Analytics', hint: 'Funnels & trends' },
  { key: 'integration', label: 'Integration', hint: 'API key + endpoints' },
];
const ACCESS_LEVELS = ['FULL', 'READ_ONLY', 'HIDDEN'];
function ConsoleAccess({ tenantId, authSession }) {
  const [access, setAccess] = useState(null); // { automation, leads, analytics }
  const [msg, setMsg] = useState(null);
  // ADMIN-CONSOLEACCESS-SWALLOWED-LOAD-WIPE [HIGH, security]: a swallowed GET failure used to default access
  // to {} which the render treats as loaded, so every screen shows as FULL (access[key]||'FULL') even for a
  // company that is actually HIDDEN/READ_ONLY — and if the admin then changes one screen, setLevel builds the
  // next map from {} and setTenantConsoleAccess PUTs the WHOLE object, dropping the company's real
  // restrictions and silently WIDENING access. Mark loaded ONLY on success; surface the error and never edit
  // against an unloaded map. Mirrors the sibling Endpoint component's loaded/loadErr guard.
  const [loaded, setLoaded] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  useEffect(() => {
    setLoaded(false); setLoadErr(null);
    getTenantConsoleAccess(tenantId, authSession)
      .then((d) => { setAccess(d?.access || {}); setLoaded(true); })
      .catch((e) => { setAccess(null); setLoadErr(e?.message || 'Could not load console access.'); });
  }, [tenantId, authSession]);
  const setLevel = async (key, level) => {
    if (!loaded) return; // never PUT against an unloaded (empty) map — it would wipe real restrictions
    const prev = access; // ADMIN-CONSOLEACCESS-OPTIMISTIC-NOREVERT: revert on failure so the failed key isn't
    const next = { ...(access || {}), [key]: level }; // carried into the next (successful) full-replace PUT
    setAccess(next);
    try { const d = await setTenantConsoleAccess(tenantId, next, authSession); setAccess(d?.access || next); setMsg('Saved'); setTimeout(() => setMsg(null), 1200); }
    catch (e) { setMsg(`Error: ${e.message}`); setAccess(prev); }
  };
  if (loadErr) return <div className="text-[11px] text-rose-600">⚠ Could not load console access ({loadErr}). Refresh before editing — saving now would overwrite this company's real screen restrictions.</div>;
  if (access === null) return <div className="text-xs text-slate-400">Loading…</div>;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Console access — screens this company may use {msg && <span className="ml-2 text-emerald-600 normal-case">· {msg}</span>}</div>
      <div className="flex flex-wrap gap-4">
        {CONSOLE_SCREENS.map((s) => (
          <label key={s.key} className="text-[11px] text-slate-500">
            <span className="block mb-0.5">{s.label} <span className="text-slate-400">· {s.hint}</span></span>
            <select className={`${inp} text-sm`} value={(access[s.key] || 'FULL')} onChange={(e) => setLevel(s.key, e.target.value)}>
              {ACCESS_LEVELS.map((l) => <option key={l} value={l}>{l === 'READ_ONLY' ? 'Read-only' : l.charAt(0) + l.slice(1).toLowerCase()}</option>)}
            </select>
          </label>
        ))}
      </div>
      <div className="text-[11px] text-slate-400 mt-1">Hidden = screen removed + data blocked · Read-only = viewable, edits blocked · Full = normal. Enforced server-side.</div>
    </div>
  );
}

// Per-company isolation tier + API endpoint (control-plane routing). SHARED = this shared API + the
// company's own schema (ecap_<tenant>); DEDICATED = the company runs its own API+DB stack, and the shared
// UI routes its data calls to that base URL. Same UI serves both tiers.
function Endpoint({ tenantId, authSession }) {
  const [tier, setTier] = useState('SHARED');
  const [url, setUrl] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  // ADMIN-ENDPOINT-SWALLOWED-LOAD: a swallowed GET failure defaulted the form to SHARED/blank, and a Save then
  // wiped a DEDICATED tenant's own-stack endpoint (routing all its data calls to the shared cluster). Mark
  // loaded ONLY on success; block Save on load failure.
  const [loaded, setLoaded] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  useEffect(() => {
    setLoaded(false); setLoadErr(null);
    getTenantEndpoint(tenantId, authSession)
      .then((d) => { setTier(d?.tier || 'SHARED'); setUrl(d?.apiBaseUrl || ''); setLoaded(true); })
      .catch((e) => setLoadErr(e?.message || 'Could not load the endpoint settings.'));
  }, [tenantId, authSession]);
  const save = async () => {
    if (!loaded) { setMsg('Refresh first — endpoint settings could not be loaded, so saving would overwrite them.'); return; }
    setBusy(true); setMsg(null);
    try {
      const d = await setTenantEndpoint(tenantId, { tier, apiBaseUrl: tier === 'DEDICATED' ? url.trim() : '' }, authSession);
      setTier(d?.tier || 'SHARED'); setUrl(d?.apiBaseUrl || '');
      clearEndpointCache(tenantId); // next data call re-resolves this company's base
      setMsg('Saved'); setTimeout(() => setMsg(null), 1500);
    } catch (e) { setMsg(`Error: ${e.message}`); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Isolation & endpoint {msg && <span className="ml-2 text-emerald-600 normal-case">· {msg}</span>}</div>
      {loadErr && <div className="text-[11px] text-rose-600 mb-1.5">⚠ Could not load the endpoint settings ({loadErr}). Refresh before editing — saving now would overwrite them.</div>}
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-[11px] text-slate-500">
          <span className="block mb-0.5">Tier</span>
          <select className={`${inp} text-sm`} value={tier} onChange={(e) => setTier(e.target.value)}>
            <option value="SHARED">Shared (this cluster + own schema)</option>
            <option value="DEDICATED">Dedicated (own API + DB stack)</option>
          </select>
        </label>
        {tier === 'DEDICATED' && (
          <label className="text-[11px] text-slate-500 flex-1 min-w-[16rem]">
            <span className="block mb-0.5">API base URL</span>
            <input className={`${inp} text-sm w-full`} value={url} placeholder="https://acme.api.movorra.ai"
              onChange={(e) => setUrl(e.target.value)} />
          </label>
        )}
        <Button size="sm" onClick={save} disabled={busy || !loaded || (tier === 'DEDICATED' && !url.trim())}>Save</Button>
      </div>
      <div className="text-[11px] text-slate-400 mt-1">Shared companies are served here; a dedicated company's data calls route to its own stack. Enforced/resolved server-side.</div>
    </div>
  );
}

// Per-company identity: the Cognito group (named after the company) that scopes its users. Normally created
// with the first user; this shows provisioned/missing and lets a super-admin provision or repair it — the
// "in case the group was missed during setup" onboarding fix. `groups` = all pool group names (null = Cognito
// unreachable, so status is unknown but repair is still offered).
function Identity({ tenantId, authSession, groups }) {
  const [status, setStatus] = useState(null); // null=unknown, true=exists, false=missing
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    // The tenant's Cognito group is movorra_<tenantId> (see TenantNames on the backend).
    setStatus(groups == null ? null : groups.includes(`movorra_${tenantId}`));
  }, [groups, tenantId]);
  const ensure = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await ensureTenantCognitoGroup(tenantId, authSession);
      setStatus(true);
      setMsg(r?.created ? 'Group created' : 'Group already present');
      setTimeout(() => setMsg(null), 1800);
    } catch (e) { setMsg(`Error: ${e.message}`); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Identity — Cognito group {msg && <span className="ml-2 text-emerald-600 normal-case">· {msg}</span>}</div>
      <div className="flex items-center gap-2">
        <code className="text-xs text-slate-600">movorra_{tenantId}</code>
        {status === true && <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">provisioned</span>}
        {status === false && <span className="text-[11px] px-2 py-0.5 rounded bg-amber-50 text-amber-700">missing</span>}
        {status === null && <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-500">unknown</span>}
        <Button size="sm" variant="outline" onClick={ensure} disabled={busy}>
          {busy ? 'Working…' : (status === false ? 'Provision group' : 'Repair')}
        </Button>
      </div>
      <div className="text-[11px] text-slate-400 mt-1">The group <code>movorra_{tenantId}</code> scopes its users to this tenant. Idempotent — safe to repair anytime.</div>
    </div>
  );
}

// Per-company secret vault (its own isolated secret store + encryption key). Normally created on
// registration; this shows provisioned/missing + key count and lets a super-admin provision or repair it
// (idempotent — the DEK is created once, never regenerated, so existing values stay intact).
function Secrets({ tenantId, authSession }) {
  const [state, setState] = useState(null); // { provisioned, keys }
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    getTenantSecrets(tenantId, authSession)
      .then((d) => setState({ provisioned: !!d?.provisioned, keys: d?.keys || [] }))
      .catch(() => setState({ provisioned: false, keys: [] }));
  }, [tenantId, authSession]);
  useEffect(() => { load(); }, [load]);
  const provision = async () => {
    setBusy(true); setMsg(null);
    try {
      const d = await provisionTenantSecrets(tenantId, authSession);
      setState({ provisioned: !!d?.provisioned, keys: d?.keys || [] });
      setMsg('Vault ready'); setTimeout(() => setMsg(null), 1800);
    } catch (e) { setMsg(`Error: ${e.message}`); }
    finally { setBusy(false); }
  };
  const provisioned = state?.provisioned;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Secrets — vault {msg && <span className="ml-2 text-emerald-600 normal-case">· {msg}</span>}</div>
      <div className="flex items-center gap-2">
        {state == null && <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-500">…</span>}
        {provisioned === true && <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">provisioned · {state.keys.length} secret{state.keys.length === 1 ? '' : 's'}</span>}
        {provisioned === false && <span className="text-[11px] px-2 py-0.5 rounded bg-amber-50 text-amber-700">missing</span>}
        <Button size="sm" variant="outline" onClick={provision} disabled={busy}>
          {busy ? 'Working…' : (provisioned === false ? 'Provision vault' : 'Repair')}
        </Button>
      </div>
      <div className="text-[11px] text-slate-400 mt-1">Each company has its own isolated, encrypted store; connections reference secrets via ${'{'}secret:KEY{'}'}. Idempotent — safe to repair anytime.</div>
    </div>
  );
}

// Per-company premium entitlements — the library monetization gate, now across ALL library types.
// The catalogue is every premium-marked item (type:id); checked = granted. A flow using an unchecked
// premium item won't publish for that company. Capabilities also show this-month usage.
const TYPE_LABEL = { connection: 'Connections', capability: 'Capabilities', policy: 'Policies', aiProfile: 'AI profiles', validationTool: 'Validation tools', domainTable: 'Domain tables' };
function Entitlements({ tenantId, authSession }) {
  const [catalog, setCatalog] = useState(null); // [type:id]
  const [granted, setGranted] = useState([]);    // [type:id]
  const [usage, setUsage] = useState({});        // capabilityId → this-month count
  const [msg, setMsg] = useState(null);
  // ADMIN-ENTITLEMENTS-SWALLOWED-CATALOG-LOAD: a swallowed catalog-load failure collapsed to [] → rendered
  // "No premium items yet", hiding this company's real premium catalogue/grants from the monetization admin.
  // Surface the error instead; keep catalog=null (Loading) distinct from [] (genuinely empty).
  const [loadErr, setLoadErr] = useState(null);
  const thisMonth = new Date().toISOString().slice(0, 7);
  useEffect(() => {
    setLoadErr(null);
    getPremiumTenant(tenantId, authSession).then((d) => { setCatalog(d?.catalog || []); setGranted(d?.granted || []); })
      .catch((e) => { setCatalog(null); setLoadErr(e?.message || 'Could not load premium entitlements.'); });
    getTenantUsage(tenantId, authSession).then((rows) => {
      const m = {};
      (rows || []).filter((r) => r.yearMonth === thisMonth).forEach((r) => { m[r.capabilityId] = r.count; });
      setUsage(m);
    }).catch(() => {});
  }, [tenantId, authSession, thisMonth]);
  const toggle = async (ref) => {
    const prev = granted; // ADMIN-ENTITLEMENTS-OPTIMISTIC-NOREVERT: revert on failure so a failed grant/revoke
    const next = granted.includes(ref) ? granted.filter((c) => c !== ref) : [...granted, ref]; // isn't persisted by a later successful toggle
    setGranted(next);
    try { const d = await setPremiumGrants(tenantId, next, authSession); setGranted(d?.granted || next); setMsg('Saved'); setTimeout(() => setMsg(null), 1200); }
    catch (e) { setMsg(`Error: ${e.message}`); setGranted(prev); }
  };
  if (loadErr) return <div className="text-xs text-rose-600">⚠ Could not load premium entitlements ({loadErr}). Refresh — the list would otherwise look empty.</div>;
  if (catalog === null) return <div className="text-xs text-slate-400">Loading…</div>;
  if (!catalog.length) return <div className="text-xs text-slate-400">No premium items yet — mark items premium in the Library.</div>;
  // Group catalogue refs by type for a readable matrix.
  const byType = {};
  catalog.forEach((ref) => { const [type, ...rest] = ref.split(':'); (byType[type] = byType[type] || []).push(rest.join(':')); });
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Premium items this company may use · usage this month {msg && <span className="ml-2 text-emerald-600 normal-case">· {msg}</span>}</div>
      <div className="space-y-2">
        {Object.keys(byType).map((type) => (
          <div key={type}>
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">{TYPE_LABEL[type] || type}</div>
            <div className="flex flex-wrap gap-4">
              {byType[type].map((id) => {
                const ref = `${type}:${id}`;
                return (
                  <label key={ref} className="flex items-center gap-1.5 text-sm text-slate-700">
                    <input type="checkbox" checked={granted.includes(ref)} onChange={() => toggle(ref)} />{id}
                    {type === 'capability' && usage[id] ? <span className="text-[11px] text-indigo-600 bg-indigo-50 px-1.5 rounded">{usage[id]} used</span> : null}
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// RESET-CONFIRM-1: an admin reset locks the account the moment it runs (Cognito RESET_REQUIRED) — the old password
// stops working until the person enters the emailed code. One stray click must not do that, so every reset confirms.
const resetConfirmProps = (u) => ({
  variant: 'danger',
  title: `Reset the password for ${u?.email}?`,
  confirmLabel: 'Reset password',
  message: 'Their current password stops working immediately. They can’t sign in again until they enter the code '
    + 'Cognito emails them and choose a new password.',
});

export function Users({ authSession, activeCompany }) {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState(''); const [tenantId, setTenantId] = useState('');
  const [error, setError] = useState(null); const [msg, setMsg] = useState(null); const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(() => new Set()); // ADMIN-USERS-RESET-DOUBLE-SUBMIT: usernames currently resetting (a Set, so an in-flight reset for one user never silently blocks another)
  const load = useCallback(() => {
    adminListUsers(authSession).then((u) => setRows(u || [])).catch((e) => setError(e.message));
  }, [authSession]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!email.trim() || !tenantId.trim()) return;
    setBusy(true); setError(null); setMsg(null);
    // MOVORRA-STAFF-1: Studio-only accounts are company Studio users — never super-admins (those are Movorra staff).
    try { await adminCreateUser(email.trim(), tenantId.trim(), false, authSession); setMsg(`Invited ${email.trim()} — Cognito emailed a temporary password.`); setEmail(''); setTenantId(''); load(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmReset, setConfirmReset] = useState(null);
  const toggleEnabled = async (u) => {
    setError(null); setMsg(null);
    try {
      await adminSetUserEnabled(u.username, !u.enabled, authSession);
      setMsg(`${u.email} ${u.enabled ? 'disabled' : 're-enabled'}.`);
      load();
    } catch (e) { setError(e.message); }
  };
  const doDelete = async () => {
    const u = confirmDelete;
    setConfirmDelete(null);
    if (!u) return;
    setError(null); setMsg(null);
    try { await adminDeleteUser(u.username, authSession); setMsg(`${u.email} deleted.`); load(); }
    catch (e) { setError(e.message); }
  };
  const reset = async (username) => {
    // ADMIN-USERS-RESET-DOUBLE-SUBMIT: guard against a double-click emailing two reset codes — but only for the
    // SAME user, so resetting one user never no-ops a legitimate reset of another (per-username Set).
    if (resetting.has(username)) return;
    setResetting((s) => new Set(s).add(username)); setError(null); setMsg(null);
    try { await adminResetPassword(username, authSession); setMsg(`Reset code emailed to ${username}.`); }
    catch (e) { setError(e.message); }
    finally { setResetting((s) => { const n = new Set(s); n.delete(username); return n; }); }
  };
  return (
    <div className="max-w-4xl">
      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={doDelete}
        variant="danger"
        title={`Permanently delete ${confirmDelete?.email}?`}
        confirmLabel="Delete login"
        message={'This removes the Cognito login for good — they lose access immediately and it cannot be '
          + 'undone. To keep the account recoverable, use “disable” instead.'}
      />
      <ConfirmDialog
        open={!!confirmReset}
        onClose={() => setConfirmReset(null)}
        onConfirm={() => { const u = confirmReset; setConfirmReset(null); if (u) reset(u.username); }}
        {...resetConfirmProps(confirmReset)}
      />
      <MovorraStaff authSession={authSession} />
      {error && <Alert tone="danger" className="mb-3 mt-8">{error}</Alert>}
      {msg && <Alert tone="success" className="mb-3 mt-8">{msg}</Alert>}
      {/* TWO-FORMS-1: the invite forms on this tab write to DIFFERENT Cognito pools. Name each for who it is for. */}
      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-800">Studio-only accounts (company Studio users)</h3>
        <p className="text-[13px] text-slate-500 mb-3">
          Studio logins scoped to one company, in the separate Studio account system. They open this Studio console
          only — not a company’s operations dashboard, dispatch board or driver app (use <em>Company staff</em> below).
          Movorra staff belong in <em>Movorra staff</em> above, not here.
        </p>
      <div className="flex gap-2 mb-4 items-end flex-wrap">
        <label className="text-xs text-slate-500">email<input className={`${inp} block mt-1`} value={email} placeholder="user@company.com" onChange={(e) => setEmail(e.target.value)} /></label>
        <label className="text-xs text-slate-500">company (tenant id)<input className={`${inp} block mt-1`} value={tenantId} placeholder="acme" onChange={(e) => setTenantId(e.target.value)} /></label>
        <Button size="sm" onClick={create} disabled={busy || !email.trim() || !tenantId.trim()}>+ Register user</Button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs"><tr><th className="text-left px-3 py-2 font-medium">Email</th><th className="text-left px-3 py-2 font-medium">Status</th><th className="text-left px-3 py-2 font-medium">Groups</th><th className="px-3 py-2"></th></tr></thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.username} className="border-t border-slate-100">
              <td className="px-3 py-2 text-slate-800">{u.email}</td>
              <td className="px-3 py-2 text-slate-500">{u.status}</td>
              <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{(u.groups || []).map((g) => <span key={g} className={`text-[11px] px-1.5 py-0.5 rounded ${g === 'movorra-admin' ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>{g}</span>)}</div></td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <button className="text-xs text-blue-600 disabled:opacity-50 mr-3" disabled={resetting.has(u.username)} onClick={() => setConfirmReset(u)}>{resetting.has(u.username) ? 'sending…' : 'reset password'}</button>
                <button className="text-xs text-slate-500 mr-3" onClick={() => toggleEnabled(u)}>{u.enabled ? 'disable' : 're-enable'}</button>
                <button className="text-xs text-rose-600" onClick={() => setConfirmDelete(u)}>delete</button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="px-3 py-3 text-slate-400">No users (or Cognito not reachable in this profile).</td></tr>}
        </tbody>
      </table>
      </div>
      <CompanyUsers key={activeCompany} authSession={authSession} initialTenant={activeCompany} />
    </div>
  );
}

// Movorra staff on the ONE login (MOVORRA-STAFF-1): ride-share pool users in movorra-admin. They sign in once and get
// Studio plus every company's applications (movorra-flows PLATFORM-SSO-1, movorra-api PLATFORM-ADMIN-1). Inviting
// an email that already has a company login just adds movorra-admin to it. No delete here — disable is reversible,
// and the backend refuses disabling your own login.
export function MovorraStaff({ authSession }) {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listMovorraStaff(authSession).then((u) => { setRows(u || []); setError(null); }).catch((e) => setError(e.message));
  }, [authSession]);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    const e = email.trim();
    if (!e || busy) return;
    setBusy(true); setError(null); setMsg(null);
    try {
      const u = await inviteMovorraStaff(e, authSession);
      setMsg(u && u.status === 'FORCE_CHANGE_PASSWORD'
        ? `Invited ${e} — Cognito emailed a temporary password. They sign in at /studio.`
        : `${e} already had a login — added to Movorra staff. They sign in at /studio with their existing password.`);
      setEmail('');
      load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const toggle = async (u) => {
    setError(null); setMsg(null);
    try {
      await setCompanyUserEnabled(u.username, !u.enabled, authSession);
      setMsg(`${u.email} ${u.enabled ? 'disabled' : 're-enabled'}.`);
      load();
    } catch (err) { setError(err.message); }
  };
  const [confirmReset, setConfirmReset] = useState(null);
  const resend = async (u) => {
    setError(null); setMsg(null);
    try { await resetCompanyUserPassword(u.username, authSession); setMsg(`Reset code emailed to ${u.email}.`); }
    catch (err) { setError(err.message); }
  };

  return (
    <div className="rounded-xl border border-indigo-200 bg-white p-4" data-testid="movorra-staff">
      <ConfirmDialog
        open={!!confirmReset}
        onClose={() => setConfirmReset(null)}
        onConfirm={() => { const u = confirmReset; setConfirmReset(null); if (u) resend(u); }}
        {...resetConfirmProps(confirmReset)}
      />
      <h3 className="text-sm font-semibold text-slate-800">Movorra staff (one login)</h3>
      <p className="text-[13px] text-slate-500 mb-3">
        Movorra administrators. One login opens Studio and every company’s applications. Invite by email; they sign in
        at /studio.
      </p>
      {error && <Alert tone="danger" className="mb-3">{error}</Alert>}
      {msg && <Alert tone="success" className="mb-3">{msg}</Alert>}
      <div className="flex gap-2 mb-4 items-end flex-wrap">
        <label className="text-xs text-slate-500">email
          <input className={`${inp} block mt-1`} value={email} placeholder="name@movorra.ai" onChange={(e) => setEmail(e.target.value)} />
        </label>
        <Button size="sm" onClick={invite} disabled={busy || !email.trim()}>+ Invite staff</Button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs"><tr>
          <th className="text-left px-3 py-2 font-medium">Email</th>
          <th className="text-left px-3 py-2 font-medium">Status</th>
          <th className="px-3 py-2"></th>
        </tr></thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.username} className="border-t border-slate-100">
              <td className="px-3 py-2 text-slate-800">{u.email}</td>
              <td className="px-3 py-2 text-slate-500">{u.enabled ? u.status : 'DISABLED'}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <button className="text-xs text-blue-600 mr-3" onClick={() => setConfirmReset(u)}>reset password</button>
                <button className="text-xs text-slate-500" onClick={() => toggle(u)}>{u.enabled ? 'disable' : 're-enable'}</button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={3} className="px-3 py-3 text-slate-400">No Movorra staff on the one login yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// Company staff — a customer company's OWN people, in the RIDE-SHARE Cognito pool.
//
// Separate from the table above on purpose. That one lists Movorra's Studio pool (internal staff). This
// one provisions the accounts a company actually uses: the operations dashboard, dispatch and
// the driver app. Those live in the ride-share pool because that is the pool movorra-api already trusts —
// Studio reaches it server-side through the AWS SDK, so no Studio token is ever trusted by movorra-api.
//
// This is what retires CloudShell: inviting a company administrator from here gives them ONE login that
// opens /r3ops and /r3dispatch, and from /r3ops they invite their own drivers and dispatchers.
// COMPANY-ROLE-SCOPE-1: Administrator ONLY. Studio can mint a Cognito login, but it cannot create the
// movorra-api domain record a driver or dispatcher needs — different service, different database, and
// Studio holds no ride-share token to call /api/admin/* with. A driver created here would sign in fine
// and then find an empty app (the driver screen loads /api/trips/by-driver-user/{sub}, and no Driver row
// would exist). Drivers and dispatchers come from /r3ops -> Manage, which writes the login AND the row
// together — which is also the intended shape: Movorra bootstraps the administrator, the company staffs
// itself. The backend enforces this too; it 400s any other role rather than creating a half account.
const COMPANY_ROLES = [
  { id: 'admin', label: 'Administrator', hint: 'operations dashboard + dispatch' },
];

function CompanyUsers({ authSession, initialTenant }) {
  const [tenant, setTenant] = useState(initialTenant || activeTenant());
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('admin');
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  // AWS reports an IAM denial as a full ARN + action, which is unreadable in a banner and buries the one
  // thing that fixes it. Translate it; the raw text stays in the console for whoever edits the policy.
  const explain = (e) => {
    const raw = (e && e.message) || 'Request failed';
    if (/not authorized to perform: cognito-idp/i.test(raw)) {
      const m = raw.match(/cognito-idp:(\w+)/);
      const action = m ? m[1] : 'Admin*';
      return 'Movorra’s server isn’t permitted to manage this Cognito pool yet (missing cognito-idp:'
        + action + '). This is a one-time AWS permission fix — company staff can’t be listed or invited '
        + 'until it is applied.';
    }
    return raw;
  };

  const load = useCallback(() => {
    if (!tenant) return;
    listCompanyUsers(tenant, authSession)
      .then((u) => { setRows(u || []); setError(null); })
      .catch((e) => setError(e.message));
  }, [tenant, authSession]);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email.trim() || busy) return;
    setBusy(true); setError(null); setMsg(null);
    try {
      await createCompanyUser(email.trim(), tenant, role, authSession);
      const label = (COMPANY_ROLES.find((r) => r.id === role) || {}).label || role;
      setMsg(`Invited ${email.trim()} as ${label} for ${tenant} — Cognito emailed a temporary password. They sign in at /studio.`);
      setEmail('');
      load();
    } catch (e) { setError(explain(e)); } finally { setBusy(false); }
  };

  const toggle = async (u) => {
    setError(null); setMsg(null);
    try {
      await setCompanyUserEnabled(u.username, !u.enabled, authSession);
      setMsg(`${u.email} ${u.enabled ? 'disabled' : 're-enabled'}.`);
      load();
    } catch (e) { setError(explain(e)); }
  };

  const [confirmDel, setConfirmDel] = useState(null);
  const doDel = async () => {
    const u = confirmDel;
    setConfirmDel(null);
    if (!u) return;
    setError(null); setMsg(null);
    try { await deleteCompanyUser(u.username, authSession); setMsg(`${u.email} deleted.`); load(); }
    catch (e) { setError(explain(e)); }
  };
  const [confirmReset, setConfirmReset] = useState(null);
  const resend = async (u) => {
    setError(null); setMsg(null);
    try { await resetCompanyUserPassword(u.username, authSession); setMsg(`Reset code emailed to ${u.email}.`); }
    catch (e) { setError(explain(e)); }
  };

  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4">
      <ConfirmDialog
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={doDel}
        variant="danger"
        title={`Permanently delete ${confirmDel?.email}?`}
        confirmLabel="Delete login"
        message={'This removes their Cognito login for good — they lose the operations '
          + 'dashboard, dispatch and the driver app immediately, and it cannot be undone. Use “disable” to keep '
          + 'the account recoverable.'}
      />
      <ConfirmDialog
        open={!!confirmReset}
        onClose={() => setConfirmReset(null)}
        onConfirm={() => { const u = confirmReset; setConfirmReset(null); if (u) resend(u); }}
        {...resetConfirmProps(confirmReset)}
      />
      <div className="flex items-baseline gap-2 mb-0.5">
        <h3 className="text-sm font-semibold text-slate-800">Company staff</h3>
        <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">{tenant}</span>
      </div>
      <p className="text-[13px] text-slate-500 mb-3">
        <strong>Invite a company’s administrator here.</strong> They sign in at <code>/studio</code> and see
        Applications and Integration, and open the operations dashboard from Applications with the same login.
        <strong> Drivers and dispatchers are added by them</strong> from the operations dashboard under Manage (that
        creates the login and the driver/dispatcher record together); drivers and dispatchers sign in at the same
        <code>/studio</code> link and go straight to their app. Only companies with applications set up (today: R3)
        have somewhere to land.
      </p>
      {error && <Alert tone="danger" className="mb-3">{error}</Alert>}
      {msg && <Alert tone="success" className="mb-3">{msg}</Alert>}
      <div className="flex gap-2 mb-4 items-end flex-wrap">
        <label className="text-xs text-slate-500">company (tenant id)
          <input className={`${inp} block mt-1`} value={tenant} onChange={(e) => setTenant(e.target.value.trim())} />
        </label>
        <label className="text-xs text-slate-500">email
          <input className={`${inp} block mt-1`} value={email} placeholder="person@company.com"
                 onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="text-xs text-slate-500">role
          <select className={`${inp} block mt-1`} value={role} onChange={(e) => setRole(e.target.value)}>
            {COMPANY_ROLES.map((r) => <option key={r.id} value={r.id}>{r.label} — {r.hint}</option>)}
          </select>
        </label>
        <Button size="sm" onClick={invite} disabled={busy || !email.trim() || !tenant}>+ Invite</Button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs"><tr>
          <th className="text-left px-3 py-2 font-medium">Email</th>
          <th className="text-left px-3 py-2 font-medium">Status</th>
          <th className="text-left px-3 py-2 font-medium">Access</th>
          <th className="px-3 py-2"></th>
        </tr></thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.username} className="border-t border-slate-100">
              <td className="px-3 py-2 text-slate-800">{u.email}</td>
              <td className="px-3 py-2 text-slate-500">{u.enabled ? u.status : 'DISABLED'}</td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {(u.groups || []).map((g) => (
                    <span key={g} className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{g}</span>
                  ))}
                </div>
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <button className="text-xs text-blue-600 mr-3" onClick={() => setConfirmReset(u)}>reset password</button>
                <button className="text-xs text-slate-500 mr-3" onClick={() => toggle(u)}>
                  {u.enabled ? 'disable' : 're-enable'}
                </button>
                <button className="text-xs text-rose-600" onClick={() => setConfirmDel(u)}>delete</button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={4} className="px-3 py-3 text-slate-400">
              No staff yet for {tenant}. Invite an Administrator to get them started.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// Runtime LLM configuration — model + sampling temperature the extraction engine uses. Applies without
// a redeploy; shows which provider is live (real OpenAI vs offline mock, which ignores the knobs).
// Billing: revenue by AI plugin per company, per month (the pay-per-service ledger).
function Billing({ authSession, activeCompany }) {
  const [rows, setRows] = useState([]);
  const [company, setCompany] = useState('');
  const [bill, setBill] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    // Default to the sidebar's company; fall back to the first one if it isn't in the list.
    listTenants(authSession).then((t) => {
      setRows(t || []);
      if (t && t.length) setCompany(t.some((x) => x.id === activeCompany) ? activeCompany : t[0].id);
    }).catch((e) => setError(e.message));
  }, [authSession, activeCompany]);
  useEffect(() => {
    if (!company) { setBill(null); return undefined; }
    // ADMIN-BILLING-LOAD-RACE: guard against out-of-order responses on a fast company switch showing one
    // company's revenue under another's name.
    let alive = true;
    setError(null);
    getTenantBilling(company, null, authSession)
      .then((b) => { if (alive) setBill(b); })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [company, authSession]);
  if (error) return <Alert tone="danger">{error}</Alert>;
  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-slate-500">Company</span>
        <select className={inp} value={company} onChange={(e) => setCompany(e.target.value)}>
          {rows.map((t) => <option key={t.id} value={t.id}>{t.name || t.id}</option>)}
        </select>
        {bill && <span className="text-xs text-slate-400">· {bill.yearMonth}</span>}
      </div>
      {!bill ? <div className="text-sm text-slate-400">Select a company.</div> : (
        <>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs"><tr>
              <th className="text-left px-3 py-2 font-medium">AI plugin</th>
              <th className="text-right px-3 py-2 font-medium">Invocations</th>
              <th className="text-right px-3 py-2 font-medium">Unit price</th>
              <th className="text-right px-3 py-2 font-medium">Revenue</th>
            </tr></thead>
            <tbody>
              {(bill.lines || []).map((l) => (
                <tr key={l.capabilityId} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-slate-800">{l.capabilityId}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{l.count}</td>
                  <td className="px-3 py-2 text-right text-slate-400">${l.unitPrice.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-medium text-emerald-700">${l.revenue.toFixed(2)}</td>
                </tr>
              ))}
              {(bill.lines || []).length === 0 && <tr><td colSpan={4} className="px-3 py-3 text-slate-400">No premium-plugin usage this month.</td></tr>}
            </tbody>
            {(bill.lines || []).length > 0 && (
              <tfoot><tr className="border-t-2 border-slate-200"><td className="px-3 py-2 font-semibold text-slate-700" colSpan={3}>Total (this month)</td>
                <td className="px-3 py-2 text-right font-bold text-emerald-700">${bill.total.toFixed(2)}</td></tr></tfoot>
            )}
          </table>
          <div className="text-[11px] text-slate-400 mt-2">Revenue = invocations × per-plugin price (set via <code>movorra.premium.prices</code>). This is the pay-per-service ledger for the AI SKUs.</div>
        </>
      )}
    </div>
  );
}

function LlmConfig({ authSession }) {
  const [cfg, setCfg] = useState(null);
  const [model, setModel] = useState('');
  const [temp, setTemp] = useState(0);
  const [ttl, setTtl] = useState(30);
  const [models, setModels] = useState([]);
  const [newModel, setNewModel] = useState('');
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const apply = (c) => { setCfg(c); setModel(c?.model || ''); setTemp(c?.temperature ?? 0); setTtl(c?.ttlDays ?? 30); setModels(c?.models || []); };
  const load = useCallback(() => {
    getLlmConfig(authSession).then(apply).catch((e) => setError(e.message));
  }, [authSession]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setError(null); setMsg(null);
    try { const c = await setLlmConfig({ model: model.trim(), temperature: Number(temp), ttlDays: Number(ttl), models }, authSession); apply(c); setMsg('Saved.'); setTimeout(() => setMsg(null), 2000); }
    catch (e) { setError(e.message); }
  };
  const addModel = () => { const m = newModel.trim(); if (m && !models.includes(m)) { setModels([...models, m]); setNewModel(''); } };
  if (error) return <Alert tone="danger">{error}</Alert>;
  if (!cfg) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="max-w-xl">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">provider: <b>{cfg.provider}</b></span>
        {cfg.live
          ? <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">live — changes affect extraction</span>
          : <span className="text-[11px] px-2 py-0.5 rounded bg-amber-50 text-amber-700">mock provider — knobs inactive (no OpenAI key)</span>}
      </div>
      {msg && <Alert tone="success" className="mb-3">{msg}</Alert>}
      <label className="block text-xs text-slate-500 mb-3">Default model
        <input className={`${inp} block mt-1 w-full`} list="llm-models" value={model} onChange={(e) => setModel(e.target.value)} placeholder="gpt-4.1-mini" />
        <datalist id="llm-models">{models.map((m) => <option key={m} value={m} />)}</datalist>
        <span className="text-[11px] text-slate-400">Pick from the catalogue below or type any valid OpenAI-compatible id.</span>
      </label>
      <label className="block text-xs text-slate-500 mb-4">Temperature — {Number(temp).toFixed(2)} <span className="text-slate-400">(0 = deterministic; higher = more variation)</span>
        <input type="range" min="0" max="2" step="0.1" value={temp} onChange={(e) => setTemp(e.target.value)} className="block mt-1 w-full" />
      </label>
      <label className="block text-xs text-slate-500 mb-4">Session TTL (days) <span className="text-slate-400">— how long a lead can resume before it's stale (default {cfg.defaultTtlDays})</span>
        <input type="number" min="1" className={`${inp} block mt-1 w-32`} value={ttl} onChange={(e) => setTtl(e.target.value)} />
      </label>
      <div className="mb-4">
        <div className="text-xs text-slate-500 mb-1">Model catalogue <span className="text-slate-400">— the models offered in the pickers (add any OpenAI model, e.g. gpt-5.4-nano)</span></div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {models.map((m) => (
            <span key={m} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 flex items-center gap-1">{m}
              <button className="text-slate-400 hover:text-rose-600" onClick={() => setModels(models.filter((x) => x !== m))}>✕</button></span>
          ))}
          {models.length === 0 && <span className="text-[11px] text-slate-400">none</span>}
        </div>
        <div className="flex gap-2">
          <input className={`${inp} flex-1`} value={newModel} placeholder="gpt-5.4-nano" onChange={(e) => setNewModel(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addModel()} />
          <Button size="sm" variant="outline" onClick={addModel} disabled={!newModel.trim()}>+ Add model</Button>
        </div>
      </div>
      <Button size="sm" onClick={save}>Save</Button>
    </div>
  );
}

// Schema-per-tenant DB migration. Each company's data lives in its own Postgres schema (ecap_<tenant>);
// when the base `ecap` schema gains a table/column (a new feature ships), the tenant schemas drift. This
// panel previews the additive, backup-first sync and applies it fleet-wide in one click. Super-admin only.
function Migration({ authSession }) {
  const [plan, setPlan] = useState(null);   // { schemas:[...], pending }
  const [result, setResult] = useState(null); // { schemas:[...], applied, failed }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [oneBusy, setOneBusy] = useState(null); // schema currently migrating solo
  const load = useCallback(() => {
    setError(null); setResult(null);
    getMigrationPlan(authSession).then(setPlan).catch((e) => setError(e.message));
  }, [authSession]);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    const pending = plan?.pending || 0;
    if (!window.confirm(`Apply ${pending} pending change(s) across ${plan?.schemas?.length || 0} tenant schema(s)?\n\nEach altered table is backed up first (<table>_bak_v<version>). This never drops data.`)) return;
    setBusy(true); setError(null);
    // ADMIN-MIGRATION-RESULT-CLOBBERED: refresh the plan but do NOT call load() — it setResult(null), instantly
    // erasing the run summary (including any "N failed") the operator needs to see.
    try { const r = await runMigration(authSession); setResult(r); getMigrationPlan(authSession).then(setPlan).catch((e) => setError(e.message)); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  // Migrate ONE company after eyeballing its plan — the "pick a company, then migrate them" flow.
  const migrateOne = async (schema, pending) => {
    if (!window.confirm(`Migrate ${schema}? ${pending} change(s), each altered table backed up first. Never drops data.`)) return;
    setOneBusy(schema); setError(null);
    try { await runMigrationFor(schema, authSession); load(); }
    catch (e) { setError(e.message); }
    finally { setOneBusy(null); }
  };
  if (error) return <div className="max-w-2xl"><Alert tone="danger">{error}</Alert></div>;
  if (!plan) return <div className="text-sm text-slate-400">Loading…</div>;
  const rows = (result?.schemas) || plan.schemas || [];
  return (
    <div className="max-w-2xl">
      <p className="text-sm text-slate-600 mb-1">
        Each company's data lives in its own schema (<code className="text-xs">ecap_&lt;tenant&gt;</code>). When a new
        feature adds a table or column to the base schema, this brings every tenant schema up to date —
        <b> additively</b> (new tables/columns only, never dropping) and <b>backup-first</b>.
      </p>
      <div className="flex items-center gap-2 my-3">
        <span className={`text-[11px] px-2 py-0.5 rounded ${plan.pending ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
          {plan.pending ? `${plan.pending} pending change(s) across ${plan.schemas.length} schema(s)` : `all ${plan.schemas.length} schema(s) up to date`}
        </span>
        <Button size="sm" variant="outline" onClick={load} disabled={busy}>Preview</Button>
        <Button size="sm" onClick={run} disabled={busy || !plan.pending}>{busy ? 'Migrating…' : 'Run migration'}</Button>
      </div>
      {result && (
        <Alert tone={result.failed ? 'warning' : 'success'} className="mb-3">
          Migrated {result.applied} schema(s){result.failed ? `, ${result.failed} failed` : ''}.
        </Alert>
      )}
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
        {rows.length === 0 && <div className="p-3 text-sm text-slate-400">No tenant schemas found.</div>}
        {rows.map((s) => (
          <div key={s.schema} className="p-3 text-sm">
            <div className="flex items-center gap-2">
              <code className="text-xs font-medium text-slate-700">{s.schema}</code>
              <span className="text-[11px] text-slate-400">v{s.version}</span>
              {s.error
                ? <span className="text-[11px] px-2 py-0.5 rounded bg-rose-50 text-rose-700">error</span>
                : s.applied
                  ? <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">applied</span>
                  : (s.statements?.length
                    ? <span className="text-[11px] px-2 py-0.5 rounded bg-amber-50 text-amber-700">{s.statements.length} pending</span>
                    : <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-500">up to date</span>)}
              {!result && s.statements?.length > 0 && (
                <button onClick={() => migrateOne(s.schema, s.statements.length)} disabled={oneBusy === s.schema}
                  className="ml-auto text-[11px] px-2 py-0.5 rounded border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50">
                  {oneBusy === s.schema ? 'Migrating…' : 'Migrate'}
                </button>
              )}
            </div>
            {s.error && <div className="text-[11px] text-rose-600 mt-1">{s.error}</div>}
            {s.statements?.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {s.statements.map((sql, i) => <li key={i} className="text-[11px] font-mono text-slate-500 truncate" title={sql}>{sql}</li>)}
              </ul>
            )}
            {s.backups?.length > 0 && (
              <div className="text-[11px] text-slate-400 mt-1">backed up: {s.backups.join(', ')}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default OpsEcapAdmin;
